// app-create-contact.component.ts

import { CommonModule } from '@angular/common';
import {
  Component,
  inject,
  InjectionToken,
  OnInit,
  signal,
  OnDestroy
} from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { FormlyFormOptions, FormlyModule } from '@ngx-formly/core';
import { FormlyPrimeNGModule } from '@ngx-formly/primeng';
import { Message } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { DropdownModule } from 'primeng/dropdown';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { MessagesModule } from 'primeng/messages';
import { map, tap } from 'rxjs';
import { AccountType, EContactType, IContact } from 'src/app/common/model/contacts.model';
import { ContactsService } from 'src/app/common/services/contacts/contacts.service';
import { UtilService } from 'src/app/common/services/util/util.service';
import { isNil, untilDestroyed } from 'src/app/common/utils';
import { CreateContactFormlyFields } from './create-contact.formly';

const DYNAMIC_DIALOG_REF = new InjectionToken<DynamicDialogRef>('dynamic-dialog-ref');
const DYNAMIC_DIALOG_CONFIG = new InjectionToken<DynamicDialogConfig>(
  'dynamic-dialog-config'
);

@Component({
  standalone: true,
  selector: 'app-create-contact',
  templateUrl: './create-contact.component.html',
  styleUrls: ['./create-contact.component.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    FormlyModule,
    FormlyPrimeNGModule,
    MessagesModule,
    ButtonModule,
    DialogModule,
    DropdownModule
  ],
  providers: [
    {
      provide: DYNAMIC_DIALOG_REF,
      useFactory: (utilService: UtilService) => {
        if (!utilService.doesURLHaveText('invoice')) {
          return new DynamicDialogRef();
        } else {
          return null;
        }
      },
      deps: [UtilService]
    },
    {
      provide: DYNAMIC_DIALOG_CONFIG,
      useFactory: (utilService: UtilService) => {
        if (!utilService.doesURLHaveText('invoice')) {
          return new DynamicDialogConfig();
        } else {
          return null;
        }
      },
      deps: [UtilService]
    }
  ]
})
export class CreateContactComponent implements OnInit, OnDestroy {
  readonly contactsService = inject(ContactsService);
  readonly activatedRoute = inject(ActivatedRoute);
  readonly utilService = inject(UtilService);
  readonly config = inject(DynamicDialogConfig);
  readonly ref = inject(DynamicDialogRef);

  readonly #untilDestroyed = untilDestroyed();

  isEdit = signal(false);
  contact!: IContact;
  organizationToAddCustomer!: IContact;
  customerToPushToOrganization!: IContact;
  type = '';
  addContactForm = new FormGroup({
    contactType: new FormControl(AccountType.ORGANIZATION),
    source: new FormControl()
  });
  model: unknown;
  options: FormlyFormOptions = {
    formState: {
      isSubCustomer: true
    }
  };
  fields = CreateContactFormlyFields;

  HTTPResponseMessage!: Message[];

  displayCustomerExistsDialog = false;

  contactSources: any[] = [
    { value: 'Walk-in', label: 'Walk-in' },
    { value: 'Referral', label: 'Referral' },
    { value: 'Instagram', label: 'Instagram' },
    { value: 'Whatsapp', label: 'Whatsapp' },
    { value: 'Facebook', label: 'Facebook' }
  ];

  contactTypes = [
    {
      value: AccountType.ORGANIZATION,
      label: 'Organization'
    },
    { value: AccountType.INDIVIDUAL, label: 'Individual' }
  ];

  contactType!: EContactType;

  ngOnInit(): void {
    this.activatedRoute.params.subscribe({
      next: (param) => {
        this.getContactType();
        const id = param['contactId'] as string | undefined;
        this.isEdit.set(!isNil(id));
        if (this.isEdit()) this.#initEditPage(id as string);
      }
    });
  }

  ngOnDestroy() {
    this.addContactForm.reset();
    this.displayCustomerExistsDialog = false;
  }

  getContactType(): void {
    if (this.config && this.config.data) {
      this.contactType = this.config.data.contactType;
    } else {
      this.contactType = this.utilService.doesURLHaveText('customer')
        ? EContactType.CUSTOMER
        : this.utilService.doesURLHaveText('vendor')
          ? EContactType.VENDOR
          : this.utilService.doesURLHaveText('insurer')
            ? EContactType.INSURER
            : EContactType.LEAD;
    }
  }

  createOrUpdateContact(): void {
    const contact = this.#getModel();

    if (this.isEdit()) {
      this.#updateContact(this.contact._id as string, contact as never).subscribe({
        next: this.#handleSubmission.bind(this)
      });
    } else {
      this.#addContact(contact as never).subscribe({
        next: this.#handleSubmission.bind(this)
      });
    }
  }

  closeExistingCustomDialog(): void {
    this.displayCustomerExistsDialog = false;
  }

  getCustomerNameExistence(): void {
    this.contactsService
      .getCustomerNameExistence((this.#getModel() as { name: string }).name)
      .subscribe({
        next: (res) => {
          if (this.isEdit()) {
            this.createOrUpdateContact();
          } else {
            if (res.result) {
              this.displayCustomerExistsDialog = true;
            } else {
              this.createOrUpdateContact();
            }
          }
        }
      });
  }

  #getModel() {
    const contact: Record<string, unknown> = {
      ...this.addContactForm.value,
      posContactType: this.contactType
    };

    return contact;
  }

  #handleSubmission(res: { result: unknown }) {
    if (this.utilService.doesURLHaveText('contacts')) {
      this.addContactForm.reset();
      this.utilService.goBack();
    } else {
      this.ref.close(res.result);
      this.closeExistingCustomDialog();
    }
  }

  #addContact(contact: IContact) {
    switch (this.contactType) {
      case EContactType.CUSTOMER:
        return this.contactsService.addCustomers(contact);

      case EContactType.VENDOR:
        return this.contactsService.addVendor(contact);

      case EContactType.INSURER:
        return this.contactsService.addInsurer(contact);

      default:
        return this.contactsService.addLead(contact);
    }
  }

  #updateContact(id: string, contact: IContact) {
    switch (this.contactType) {
      case EContactType.CUSTOMER:
        return this.contactsService.updateCustomer(id, contact);

      case EContactType.VENDOR:
        return this.contactsService.updateVendor(id, contact);

      case EContactType.INSURER:
        return this.contactsService.updateInsurer(id, contact);

      default:
        return this.contactsService.updateLead(id, contact);
    }
  }

  #getContact(id: string) {
    switch (this.contactType) {
      case EContactType.CUSTOMER:
        return this.contactsService.getCustomerById(id);

      case EContactType.VENDOR:
        return this.contactsService.getVendorById(id);

      case EContactType.INSURER:
        return this.contactsService.getInsurerById(id);

      default:
        return this.contactsService.getLeadById(id);
    }
  }

  #initEditPage(contactId: string) {
    const updateData = () =>
      tap({
        next: (data: IContact) => {
          this.contact = data;
          this.model = data;
          (this.model as { assignedTo: string[] }).assignedTo = (
            data.assignedTo ?? []
          ).map((x: any) => x._id);
          this.addContactForm.patchValue(data as never);
        }
      });

    this.#getContact(contactId)
      .pipe(
        map((res) => res.result),
        updateData(),
        this.#untilDestroyed()
      )
      .subscribe();
  }
}
